<script lang="ts">
  // Pick and customise a social report: type, template, size, background, accent colour and
  // which fields show, and download or share it as an image (for Instagram and the like).
  //  - mode "dialog" (the Send to Strava dialog): loads the activity's data from `path` and
  //    exposes the design as `config`, sent with the activity. Only type, design and background
  //    show until "Customize" is opened. Never changes the saved designs, and photos uploaded
  //    here are for this report only (not added to the saved backgrounds).
  //  - mode "page" (the Activity Reports page): previews with `preview` and saves the design as
  //    the default for its report type, which automatic Strava sends use. Everything shows.
  //    The only place the saved designs and backgrounds change.
  //  - mode "share" (ShareActivityButton): like the dialog, but for posting the image anywhere
  //    other than Strava. Starts at the Instagram post size, and leaves the Share / Download /
  //    Copy buttons to the popup's footer (download(), share() and copy() below, with the
  //    bindable export state).
  import { onMount } from 'svelte'
  import { Button } from '@/lib/components/ui/button'
  import { Checkbox } from '@/lib/components/ui/checkbox'
  import { Label } from '@/lib/components/ui/label'
  import { Slider } from '@/lib/components/ui/slider'
  import { cn } from '@/lib/utils'
  import { deleteSocialReportBackground, getSocialReportData, renderSocialReportImage, saveSocialReportDefault, uploadSocialReportBackground } from '@/Util/api'
  import { notifyApiError } from '@/Util/notify'
  import { ErrorCode } from '@/Util/errorCodes'
  import ReportPreview from './ReportPreview.svelte'
  import { shrinkImage } from './image'
  import { ACCENTS, DEFAULT_SIZE, REPORT_SIZES, SHARE_DEFAULT_SIZE, SHARE_SIZE_ORDER, SIZE_ORDER, fieldAvailable, normalizeOptions, resolveBackground, type FieldGroup, type TemplateDef } from './context'
  import { TEMPLATES_BY_ID, TYPE_LABELS, TYPE_ORDER, templateAvailable, templatesFor } from './registry'
  import { POWER_LABELS, powerChoice, powerMetricsFor } from './stats'
  import type { Accent, BackgroundKind, ReportConfig, ReportData, ReportOptions, ReportSettings, ReportSize, ReportType, UploadedBackground } from './types'

  export let mode: 'dialog' | 'page' | 'share' = 'dialog'
  /** Dialog and share: the activity whose report this is. */
  export let path: string | null = null
  /**
   * Page: preview data (with sample sections for every type) and the saved designs, plus the
   * activity downloads are made from, its own data, and the report types it really has.
   */
  export let preview: { data: ReportData, settings: ReportSettings, path: string | null, real: ReportData | null, types: ReportType[] } | null = null
  /** The chosen design, or null until it has loaded. */
  export let config: ReportConfig | null = null
  /** Report type to start on; defaults to the activity's recommended type. */
  export let initialType: ReportType | null = null
  export let disabled = false
  // Share: the export state, for the popup's footer buttons.
  export let exportBlocked: string | null = null
  export let exporting: 'download' | 'share' | 'copy' | null = null
  export let shareNeedsTap = false

  const page = mode === 'page'
  const sharing = mode === 'share'
  // SocialReportSettings::MAX_BACKGROUNDS
  const MAX_BACKGROUNDS = 12

  let data: ReportData | null = null
  let settings: ReportSettings | null = null
  let loadError: string | null = null
  let type: ReportType = 'activity'
  let templateId = ''
  let size: ReportSize = DEFAULT_SIZE
  // Edits per template, so switching templates back and forth keeps them.
  let optionsById: Record<string, ReportOptions> = {}
  let customizing = page
  let uploading = false
  // Dialog and share: photos uploaded for this report only.
  let temporaryBackgrounds: UploadedBackground[] = []
  let saving = false
  let fileInput: HTMLInputElement
  // The last image made for download/share, so sharing it again (or after downloading) is instant.
  let rendered: { key: string, file: File } | null = null
  /** Whether this browser can hand image files to the system share sheet (phones, mostly). */
  export const canShareFiles = (() => {
    try {
      return !!navigator.canShare?.({ files: [new File([''], 'report.jpg', { type: 'image/jpeg' })] })
    } catch {
      return false
    }
  })()

  onMount(load)

  // Page: another activity was picked. Keep the design; only the data changes.
  $: if (preview && data && preview.data !== data) {
    data = preview.data
    // TEMPLATES_BY_ID, not `def`: reading `def` here would order this block after `$: def`,
    // which then wouldn't see the template selectType() picks.
    const current = TEMPLATES_BY_ID[templateId]
    if (current && !templateAvailable(current, dataFor(type)!)) selectType(type)
  }

  async function load() {
    loadError = null
    if (preview) {
      data = preview.data
      settings = preview.settings
    } else if (path) {
      const res = await getSocialReportData(path)
      if (!res.success) {
        loadError = res.message ?? 'Could not load the report designs.'
        return
      }
      data = res.data
      settings = res.settings
    }
    if (!data) return
    const start = initialType && data.types.includes(initialType) ? initialType : data.recommended_type
    selectType(start)
  }

  // Page: report types the previewed activity really has are drawn from its own data, as its
  // downloads are, so the preview matches the image. Samples stand in only for the other types.
  function dataFor(t: ReportType): ReportData | null {
    return page && preview?.real && preview.types.includes(t) ? preview.real : data
  }

  function savedFor(t: ReportType): TemplateDef | null {
    return settings ? TEMPLATES_BY_ID[settings.default_templates[t]] ?? null : null
  }

  function selectType(t: ReportType) {
    if (!data || !settings) return
    type = t
    const d = dataFor(t)!
    const preferred = savedFor(t)
    const choice = preferred && templateAvailable(preferred, d) ? preferred : templatesFor(t).find((def) => templateAvailable(def, d))
    if (choice) {
      // On the Reports page each type opens at its saved size; in the dialog the size carries over.
      // Sharing starts at the Instagram post size: the saved sizes suit Strava.
      if (page || !templateId) size = sharing && !templateId ? SHARE_DEFAULT_SIZE : normalizeOptions(choice, settings.template_options[choice.id]).size
      selectTemplate(choice)
    }
  }

  function selectTemplate(def: TemplateDef) {
    templateId = def.id
    if (!optionsById[def.id]) {
      optionsById[def.id] = normalizeOptions(def, settings?.template_options[def.id])
    }
    // Sizes follow the size control, not the template.
    optionsById[def.id] = { ...optionsById[def.id], size }
  }

  function setSize(s: ReportSize) {
    size = s
    update({ size: s })
  }

  $: def = TEMPLATES_BY_ID[templateId]
  $: options = optionsById[templateId]
  $: config = def && options ? { template_id: def.id, options } : null
  $: types = data ? TYPE_ORDER.filter((t) => data!.types.includes(t)) : []
  // What the preview and design thumbnails draw (see dataFor).
  $: shown = (page && preview?.real && preview.types.includes(type) ? preview.real : data)!

  // Downloads and shares are rendered from a real activity, so only for types it has.
  $: imagePath = page ? preview?.path ?? null : path
  $: imageTypes = page ? preview?.types ?? [] : data?.types ?? []
  $: exportBlocked = !imagePath
    ? 'Record an activity to download your reports.'
    : !imageTypes.includes(type) ? `This activity has no ${TYPE_LABELS[type]} report, so this preview uses sample values. Pick an activity that has one to download it.` : null
  $: exportKey = imagePath && config ? `${imagePath}:${JSON.stringify(config)}` : null
  $: if (rendered?.key !== exportKey) shareNeedsTap = false

  // Page: whether the shown design differs from the saved default for this type.
  $: saved = settings && def ? savedFor(type) : null
  $: dirty = !!def && !!options && (!saved || saved.id !== def.id || JSON.stringify(normalizeOptions(def, settings?.template_options[def.id])) !== JSON.stringify(options))

  function update(patch: Partial<ReportOptions>) {
    optionsById[templateId] = { ...optionsById[templateId], ...patch }
  }

  function setField(key: string, on: boolean) {
    update({ fields: { ...options.fields, [key]: on } })
  }

  $: fieldOn = def && options ? Object.fromEntries(def.fields.map((f) => [f.key, options.fields[f.key] ?? f.default ?? true])) as Record<string, boolean> : {}

  function setAccent(key: string) {
    update({ accent: key as Accent })
  }

  function setBackground(kind: BackgroundKind, url: string | null = null) {
    if (kind === bgSelected.kind && url === bgSelected.url) return
    update({ background: { kind, url } })
  }

  // Templates where the route map and power chart share a spot: which one shows (as drawn: the
  // route wins when both fields are on), and the choices this activity allows.
  $: graphicChoices = (() => {
    if (!def?.routeOrChart || !shown) return []
    const ok = (key: string) => def.fields.some((f) => f.key === key && fieldAvailable(f, shown))
    return [
      ...(ok('route') ? [{ key: 'route', label: 'Route map' }] : []),
      ...(ok('chart') ? [{ key: 'chart', label: 'Power chart' }] : []),
      { key: 'none', label: 'None' },
    ]
  })()
  $: graphic = !def?.routeOrChart ? null
    : fieldOn['route'] && graphicChoices.some((c) => c.key === 'route') ? 'route'
    : fieldOn['chart'] && graphicChoices.some((c) => c.key === 'chart') ? 'chart'
    : 'none'

  function setGraphic(key: string) {
    update({ fields: { ...options.fields, route: key === 'route', chart: key === 'chart' } })
  }

  $: groups = (() => {
    if (!def || !shown || !options) return []
    const order: FieldGroup[] = ['Content', 'Graphics', 'Badges']
    const hidden = new Set([
      ...(def.routeOrChart ? ['route', 'chart'] : []),
      // Coloured power charts always draw MPA, as the activity charts do.
      ...(options.colorPowerChart ? ['mpa'] : []),
    ])
    return order
      .map((g) => ({ group: g, fields: def.fields.filter((f) => f.group === g && !hidden.has(f.key) && fieldAvailable(f, shown)) }))
      .filter((g) => g.fields.length || (g.group === 'Graphics' && graphicChoices.length > 1))
  })()

  $: usesPhoto = !!options && !!data && (
    options.background.kind === 'stock' || options.background.kind === 'upload' || options.background.kind === 'streetview' ||
    (options.background.kind === 'default' && def?.defaultBackground === 'photo')
  )
  $: showsRoute = !!data?.route && !!def?.fields.some((f) => f.key === 'route') && options && fieldOn['route']
  $: hasHr = !!shown?.series?.hr?.some((v) => typeof v === 'number')
  // The power choice: which figures this activity has, in either unit.
  $: powerChoices = shown && def?.fields.some((f) => f.key === 'stat.powerChoice') && fieldOn['stat.powerChoice']
    ? powerMetricsFor(shown, 'w').map((m) => ({ key: m, label: POWER_LABELS[m].choice }))
    : []
  $: powerUnits = shown && options ? (['w', 'wkg'] as const).filter((u) => !!power && powerMetricsFor(shown, u).includes(power.metric)) : []
  $: power = shown && options ? powerChoice(shown, options) : null
  $: showsPowerChart = !!shown?.series?.power && !!def?.fields.some((f) => f.key === 'chart') && options && fieldOn['chart'] && (!graphic || graphic === 'chart')
  // The page previews with sample data, so street view is offered as "your ride's street view".
  $: streetviewThumb = data?.backgrounds.streetview ?? null
  $: offersStreetview = !!streetviewThumb || page
  // The swatch to highlight. The template's default background has no swatch of its own: it's
  // street view (or the stock photo that stands in for it) for photo templates, no photo otherwise.
  $: bgSelected = (() => {
    const bg = options?.background ?? { kind: 'default', url: null }
    if (bg.kind !== 'default') return bg
    if (def?.defaultBackground !== 'photo') return { kind: 'none', url: null }
    if (offersStreetview) return { kind: 'streetview', url: null }
    return { kind: 'stock', url: data && def && options ? resolveBackground(def, data, options) : null }
  })() as { kind: BackgroundKind, url: string | null }

  async function save() {
    if (!config) return
    saving = true
    const res = await saveSocialReportDefault(config)
    saving = false
    if (!res.success) {
      notifyApiError(res, { code: ErrorCode.SOCIAL_REPORT_SAVE_FAILED, message: 'Could not save your report design.' })
      return
    }
    settings = { ...res.settings, backgrounds: settings?.backgrounds ?? res.settings.backgrounds }
  }

  async function imageFile(): Promise<File | null> {
    if (!imagePath || !config || !exportKey) return null
    if (rendered?.key === exportKey) return rendered.file
    const key = exportKey
    const res = await renderSocialReportImage(imagePath, config)
    if (!res.success || !res.file) {
      notifyApiError(res, { code: ErrorCode.SOCIAL_REPORT_IMAGE_FAILED, message: 'Could not create the report image.' })
      return null
    }
    rendered = { key, file: res.file }
    return res.file
  }

  export async function download() {
    exporting = 'download'
    const file = await imageFile()
    exporting = null
    if (!file) return
    const url = URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10000)
  }

  export async function share() {
    const wasReady = rendered?.key === exportKey
    exporting = 'share'
    const file = await imageFile()
    exporting = null
    if (!file) return
    try {
      await navigator.share({ files: [file] })
      shareNeedsTap = false
    } catch (e) {
      // Browsers only open the share sheet straight from a tap, and making the image can take
      // longer than they allow. It's ready now, so the next tap shares it immediately.
      if ((e as DOMException)?.name === 'NotAllowedError' && !wasReady) shareNeedsTap = true
    }
  }

  /** Copy the image to the clipboard. Resolves to whether it was copied. */
  export async function copy(): Promise<boolean> {
    exporting = 'copy'
    try {
      // Clipboards only take PNG images. The item is created straight away with a promise of
      // the image, since Safari only allows writes started directly from the tap.
      const png = imageFile().then((file) => {
        if (!file) throw new Error('No image')
        return toPng(file)
      })
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })])
      return true
    } catch (e) {
      if ((e as Error)?.message !== 'No image') {
        notifyApiError(null, { code: ErrorCode.SOCIAL_REPORT_IMAGE_FAILED, message: 'Could not copy the image. Try Download instead.' })
      }
      return false
    } finally {
      exporting = null
    }
  }

  async function toPng(file: File): Promise<Blob> {
    const bitmap = await createImageBitmap(file)
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!blob) throw new Error('Could not convert the image')
    return blob
  }

  async function onUpload(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0]
    fileInput.value = ''
    if (!file || !settings) return
    uploading = true
    const res = await uploadSocialReportBackground(await shrinkImage(file), !page)
    uploading = false
    if (!res.success) {
      notifyApiError(res, { code: ErrorCode.SOCIAL_REPORT_BACKGROUND_FAILED, message: 'Could not upload that image.' })
      return
    }
    if (!page) {
      temporaryBackgrounds = [res.background, ...temporaryBackgrounds]
      setBackground('upload', res.background.url)
      return
    }
    // The server keeps the newest MAX_BACKGROUNDS uploads and deletes the rest.
    const backgrounds = [res.background, ...settings.backgrounds]
    for (const old of backgrounds.slice(MAX_BACKGROUNDS)) forgetBackground(old.url)
    settings.backgrounds = backgrounds.slice(0, MAX_BACKGROUNDS)
    setBackground('upload', res.background.url)
  }

  async function onDeleteBackground(bg: UploadedBackground) {
    if (!settings) return
    if (bg.temporary) {
      // The server deletes it once it expires.
      temporaryBackgrounds = temporaryBackgrounds.filter((b) => b.id !== bg.id)
      forgetBackground(bg.url)
      return
    }
    const res = await deleteSocialReportBackground(bg.id)
    if (!res.success) {
      notifyApiError(res, { code: ErrorCode.SOCIAL_REPORT_BACKGROUND_FAILED, message: 'Could not remove that background.' })
      return
    }
    settings.backgrounds = settings.backgrounds.filter((b) => b.id !== bg.id)
    forgetBackground(bg.url)
  }

  // Templates that used a removed upload go back to their default background.
  function forgetBackground(url: string) {
    for (const [tid, opts] of Object.entries(optionsById)) {
      if (opts.background.kind === 'upload' && opts.background.url === url) {
        optionsById[tid] = { ...opts, background: { kind: 'default', url: null } }
      }
    }
  }

  const swatch = 'relative h-14 w-11 flex-none overflow-hidden rounded-md border-2 bg-muted bg-cover bg-center transition'
  // Thumbnails share a height, so landscape ones are wider.
  const THUMB_HEIGHT = page ? 132 : 110
</script>

{#if loadError}
  <div class="flex flex-row items-center gap-2 text-sm text-red-600">
    <span>{loadError}</span>
    <button type="button" class="underline" on:click={load}>Retry</button>
  </div>
{:else if !data || !def || !options}
  <div class="flex flex-row items-center gap-2 text-sm">
    <i class="fa-solid fa-spinner-third fa-spin text-gray-400" />
    <div>Loading report designs...</div>
  </div>
{:else}
  <div class={cn('grid gap-5', page ? 'lg:grid-cols-[minmax(0,1.15fr),minmax(0,1fr)] lg:gap-8' : 'sm:grid-cols-[minmax(0,300px),minmax(0,1fr)]', disabled && 'pointer-events-none opacity-50 grayscale')}>
    <!-- Preview, with the size control under it -->
    <div class={cn('mx-auto w-full', page ? 'max-w-[640px] lg:sticky lg:top-4 lg:self-start' : 'max-w-[300px] sm:sticky sm:top-0 sm:self-start')}>
      <!-- The tall stage only on wide screens: on phones it leaves a gap above a square preview. -->
      <div class={cn('flex items-center justify-center', page && 'lg:min-h-[min(640px,70vh)]')}>
        <div class="w-full" style="max-width: {{ portrait: page ? 512 : 240, square: page ? 600 : 300, landscape: 9999, story: page ? 360 : 180 }[size]}px;">
          <ReportPreview data={shown} templateId={def.id} {options} class="rounded-lg shadow-md ring-1 ring-border" />
        </div>
      </div>
      <div class="mt-3 flex justify-center">
        <!-- Sharing names sizes by where they're posted; those labels are longer, so two columns. -->
        <div class={cn('rounded-lg border border-border p-0.5', sharing ? 'grid w-full grid-cols-2 gap-0.5' : 'inline-flex flex-wrap justify-center')} role="radiogroup" aria-label="Image size">
          {#each sharing ? SHARE_SIZE_ORDER : SIZE_ORDER as s}
            {@const dims = REPORT_SIZES[s]}
            <button type="button" role="radio" aria-checked={s === size} on:click={() => setSize(s)}
              title="{dims.label} {dims.ratio}: {dims.bestFor.toLowerCase()}"
              class={cn('flex items-center gap-1.5 rounded-md py-1.5 text-xs font-medium transition', page ? 'px-3' : 'px-2', sharing && 'justify-center', s === size ? 'bg-orange-500 text-white' : 'text-muted-foreground hover:bg-muted')}>
              <span class="inline-block flex-none rounded-[2px] border-[1.5px] border-current" style="width: {(dims.width / dims.height) * 12}px; height: 12px;" />
              {sharing ? dims.shareLabel : dims.label}
              {#if page || sharing}<span class="opacity-70">{dims.ratio}</span>{/if}
            </button>
          {/each}
        </div>
      </div>
      <p class="mt-2 text-center text-xs text-muted-foreground">
        {REPORT_SIZES[size].width} × {REPORT_SIZES[size].height}
        <span class="font-medium text-foreground">· {REPORT_SIZES[size].bestFor}</span>
      </p>
      {#if !sharing}
      <div class="mt-3 flex flex-wrap justify-center gap-2">
        <Button variant="outline" size="sm" disabled={!!exportBlocked || !!exporting} title={exportBlocked ?? 'Save the image to post it anywhere'} on:click={download}>
          <i class={cn('fa-solid mr-2 text-xs', exporting === 'download' ? 'fa-spinner-third fa-spin' : 'fa-download')} />
          {exporting === 'download' ? 'Creating image...' : 'Download'}
        </Button>
        {#if canShareFiles}
          <Button variant="outline" size="sm" disabled={!!exportBlocked || !!exporting} title={exportBlocked ?? 'Share to Instagram and other apps'} on:click={share}>
            <i class={cn('fa-solid mr-2 text-xs', exporting === 'share' ? 'fa-spinner-third fa-spin' : 'fa-share-nodes')} />
            {exporting === 'share' ? 'Creating image...' : shareNeedsTap ? 'Image ready, tap to share' : 'Share'}
          </Button>
        {/if}
      </div>
      {#if exportBlocked && imagePath}
        <p class="mt-1 text-center text-xs text-muted-foreground">{exportBlocked}</p>
      {/if}
      {/if}
      {#if page && !imagePath}
        <p class="mt-1 text-center text-xs text-muted-foreground">Previewed with sample values.</p>
      {/if}
    </div>

    <!-- Controls -->
    <div class="flex min-w-0 flex-col gap-4">
      <section class="space-y-2">
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground">{page ? 'Report type' : 'Report'}</h3>
        <div class="flex flex-wrap gap-1.5">
          {#each types as t}
            <button type="button" on:click={() => selectType(t)}
              class={cn('rounded-full border px-3 py-1 text-sm transition', t === type ? 'border-orange-500 bg-orange-500 text-white' : 'border-border hover:bg-muted')}>
              {TYPE_LABELS[t]}{mode === 'dialog' && t === data.auto_type ? ' ★' : ''}
            </button>
          {/each}
        </div>
      </section>

      <section class="space-y-2">
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Design</h3>
        <div class="flex flex-wrap gap-3">
          {#each templatesFor(type) as t}
            {@const ok = templateAvailable(t, shown)}
            {@const dims = REPORT_SIZES[size]}
            <button type="button" disabled={!ok} on:click={() => selectTemplate(t)} title={ok ? t.description : t.unavailableReason}
              class={cn('text-left transition', !ok && 'cursor-not-allowed opacity-40')} style="width: {(THUMB_HEIGHT * dims.width) / dims.height}px;">
              <div class={cn('relative overflow-hidden rounded-md border-2', t.id === def.id ? 'border-orange-500' : 'border-transparent')}>
                {#if ok}
                  <ReportPreview data={shown} templateId={t.id} options={{ ...(optionsById[t.id] ?? normalizeOptions(t, settings?.template_options[t.id])), size }} />
                {:else}
                  <div class="flex items-center justify-center bg-muted p-1 text-center text-[10px] leading-tight text-muted-foreground" style="height: {THUMB_HEIGHT}px;">{t.unavailableReason ?? 'Not available for this activity'}</div>
                {/if}
              </div>
              <div class="mt-1 flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs font-medium leading-tight">
                {t.name}
                {#if page && savedFor(type)?.id === t.id}<span class="rounded bg-muted px-1 text-[10px] text-muted-foreground">Default</span>{/if}
              </div>
            </button>
          {/each}
        </div>
        <p class="text-xs text-muted-foreground">{def.description}</p>
      </section>

      <section class="space-y-2">
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Background</h3>
        <div class="flex flex-wrap gap-2">
          <button type="button" title="No photo" on:click={() => setBackground('none')}
            class={cn(swatch, 'flex items-center justify-center', bgSelected.kind === 'none' ? 'border-orange-500' : 'border-transparent')}>
            <i class="fa-solid fa-ban text-muted-foreground" />
          </button>
          {#if offersStreetview}
            <button type="button" title="Street view from your ride" on:click={() => setBackground('streetview')}
              style={streetviewThumb ? `background-image: url('${streetviewThumb}');` : ''}
              class={cn(swatch, 'flex items-center justify-center', bgSelected.kind === 'streetview' ? 'border-orange-500' : 'border-transparent')}>
              {#if !streetviewThumb}<span class="text-[10px] leading-tight text-muted-foreground">Street view</span>{/if}
              <i class="fa-solid fa-location-dot absolute bottom-0.5 right-1 text-[10px] text-white drop-shadow" />
            </button>
          {/if}
          {#each data.backgrounds.stock as url}
            <button type="button" title="Stock photo" on:click={() => setBackground('stock', url)} style="background-image: url('{url}');"
              class={cn(swatch, bgSelected.kind === 'stock' && bgSelected.url === url ? 'border-orange-500' : 'border-transparent')} />
          {/each}
          <!-- Saved photos are removed on the Activity Reports page; ones uploaded for this report just leave the list. -->
          {#each [...temporaryBackgrounds, ...settings?.backgrounds ?? []] as bg (bg.id)}
            <div class="group relative">
              <button type="button" title={bg.temporary ? 'Your photo, for this report only' : 'Your photo'} on:click={() => setBackground('upload', bg.url)} style="background-image: url('{bg.url}');"
                class={cn(swatch, bgSelected.kind === 'upload' && bgSelected.url === bg.url ? 'border-orange-500' : 'border-transparent')} />
              {#if page || bg.temporary}
                <button type="button" title="Remove this photo" aria-label="Remove this photo" on:click={() => onDeleteBackground(bg)}
                  class="absolute -right-1.5 -top-1.5 hidden h-5 w-5 items-center justify-center rounded-full bg-background text-[10px] shadow ring-1 ring-border group-focus-within:flex group-hover:flex [@media(hover:none)]:flex">
                  <i class="fa-solid fa-xmark" />
                </button>
              {/if}
            </div>
          {/each}
          <button type="button" title={page ? 'Upload your own photo' : 'Upload a photo for this report'} disabled={uploading} on:click={() => fileInput.click()}
            class={cn(swatch, 'flex items-center justify-center border-dashed border-border')}>
            <i class={cn('fa-solid text-muted-foreground', uploading ? 'fa-spinner-third fa-spin' : 'fa-plus')} />
          </button>
          <input bind:this={fileInput} type="file" accept="image/jpeg,image/png,image/webp" class="hidden" on:change={onUpload} />
        </div>
      </section>

      {#if !page}
        <button type="button" on:click={() => (customizing = !customizing)} aria-expanded={customizing}
          class="flex w-fit items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium transition hover:bg-muted">
          <i class="fa-solid fa-sliders text-xs" />
          {customizing ? 'Hide customization' : 'Customize'}
          <i class={cn('fa-solid fa-chevron-down text-[10px] transition-transform', customizing && 'rotate-180')} />
        </button>
      {/if}

      {#if customizing}
        {#if usesPhoto}
          <section class="space-y-2">
            <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Photo darkness</h3>
            <Slider min={0} max={100} step={5} value={[options.overlay]} onValueChange={(v) => update({ overlay: v[0] })} />
          </section>
        {/if}

        <section class="space-y-2">
          <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Accent</h3>
          <div class="flex flex-wrap gap-2">
            {#each Object.entries(ACCENTS) as [key, accent]}
              <button type="button" title={accent.label} on:click={() => setAccent(key)}
                class={cn('h-7 w-7 rounded-full border-2 transition', options.accent === key ? 'border-foreground scale-110' : 'border-transparent')}
                style="background: {accent.color};" />
            {/each}
          </div>
        </section>

        {#each groups as g}
          <section class="space-y-2">
            <h3 class="text-sm font-medium tracking-wide text-muted-foreground">{g.group === 'Badges' ? 'Also show' : g.group}</h3>
            {#if g.group === 'Graphics' && graphicChoices.length > 1}
              <div class="inline-flex flex-wrap rounded-lg border border-border p-0.5" role="radiogroup" aria-label="Route map or power chart">
                {#each graphicChoices as c (c.key)}
                  <button type="button" role="radio" aria-checked={graphic === c.key} on:click={() => setGraphic(c.key)}
                    class={cn('rounded-md px-3 py-1.5 text-xs font-medium transition', graphic === c.key ? 'bg-orange-500 text-white' : 'text-muted-foreground hover:bg-muted')}>
                    {c.label}
                  </button>
                {/each}
              </div>
            {/if}
            <div class="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
              {#each g.fields as f (f.key)}
                <div class="flex items-center gap-2">
                  <Checkbox id="rf-{mode}-{f.key}" checked={fieldOn[f.key]} onCheckedChange={(v) => setField(f.key, v === true)} />
                  <Label class="m-0 text-sm font-normal" for="rf-{mode}-{f.key}">{f.label}</Label>
                </div>
              {/each}
            </div>
            {#if g.group === 'Content' && powerChoices.length}
              <div class="space-y-2 pt-1">
                <span class="text-sm">Power</span>
                <div class="flex flex-wrap items-center gap-2">
                  <div class="inline-flex flex-wrap rounded-lg border border-border p-0.5" role="radiogroup" aria-label="Power figure">
                    {#each powerChoices as c (c.key)}
                      <button type="button" role="radio" aria-checked={power?.metric === c.key} on:click={() => update({ powerMetric: c.key })}
                        class={cn('rounded-md px-3 py-1.5 text-xs font-medium transition', power?.metric === c.key ? 'bg-orange-500 text-white' : 'text-muted-foreground hover:bg-muted')}>
                        {c.label}
                      </button>
                    {/each}
                  </div>
                  {#if powerUnits.length > 1}
                    <div class="inline-flex rounded-lg border border-border p-0.5" role="radiogroup" aria-label="Power unit">
                      {#each powerUnits as u (u)}
                        <button type="button" role="radio" aria-checked={power?.unit === u} on:click={() => update({ powerUnit: u })}
                          class={cn('rounded-md px-3 py-1.5 text-xs font-medium transition', power?.unit === u ? 'bg-orange-500 text-white' : 'text-muted-foreground hover:bg-muted')}>
                          {u === 'w' ? 'W' : 'W/kg'}
                        </button>
                      {/each}
                    </div>
                  {/if}
                </div>
              </div>
            {/if}
            {#if g.group === 'Graphics' && showsPowerChart}
              <div class="flex items-start gap-2 pt-1">
                <Checkbox id="rf-{mode}-colors" checked={options.colorPowerChart} onCheckedChange={(v) => update({ colorPowerChart: v === true })} />
                <Label class="m-0 text-sm font-normal leading-snug" for="rf-{mode}-colors">Colour the power chart by intensity</Label>
              </div>
              {#if options.colorPowerChart && hasHr}
                <div class="flex items-start gap-2 pl-6">
                  <Checkbox id="rf-{mode}-hr" checked={options.chartHeartRate} onCheckedChange={(v) => update({ chartHeartRate: v === true })} />
                  <Label class="m-0 text-sm font-normal leading-snug" for="rf-{mode}-hr">Show heart rate</Label>
                </div>
              {/if}
            {/if}
            {#if g.group === 'Graphics' && showsRoute}
              <div class="flex items-start gap-2 pt-1">
                <Checkbox id="rf-{mode}-trim" checked={options.trimRoute} onCheckedChange={(v) => update({ trimRoute: v === true })} />
                <Label class="m-0 text-sm font-normal leading-snug" for="rf-{mode}-trim">
                  Trim route start and end
                  <span class="block text-xs text-muted-foreground">Hides the first and last 500 m so the map doesn't show where you start and finish.</span>
                </Label>
              </div>
            {/if}
          </section>
        {/each}
      {/if}

      {#if page}
        <!-- Fixed on phones: iOS Safari leaves a sticky bottom bar where it was when its toolbar
             collapses, with the page showing beneath it. Fixed bars follow the visible bottom. -->
        <div class="h-16 lg:hidden" />
        <div class="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-border bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:sticky lg:z-auto lg:px-0 lg:pb-3">
          <Button class="bg-orange-500 hover:bg-orange-400 max-sm:w-full" disabled={!dirty || saving} on:click={save}>
            <i class={cn('fa-solid mr-2 text-xs', saving ? 'fa-spinner-third fa-spin' : dirty ? 'fa-floppy-disk' : 'fa-check')} />
            {dirty ? `Save as my default ${TYPE_LABELS[type]} report` : 'Saved'}
          </Button>
          <span class="hidden text-xs text-muted-foreground sm:inline">
            {#if type === 'activity'}
              Used when you add an Activity report to a ride.
            {:else}
              Reports sent to Strava automatically use your saved {TYPE_LABELS[type]} design.
            {/if}
          </span>
        </div>
      {:else if !sharing}
        <!-- Changes here are for this report only; the saved designs are set on the Activity Reports page. -->
        <div class="flex items-start gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm">
          <i class="fa-solid fa-palette mt-0.5 text-orange-500" aria-hidden="true" />
          <p class="m-0 leading-snug">
            Want this look on every report?
            <a href="/social-reports" target="_blank" rel="noopener" class="font-medium text-orange-600 underline hover:text-orange-500">Set up your default reports</a>
            <span class="block text-xs text-muted-foreground">
              Changes here only apply to this report.{type !== 'activity' ? ` Automatic sends to Strava use your saved ${TYPE_LABELS[type]} design.` : ''}
            </span>
          </p>
        </div>
      {/if}
    </div>
  </div>
{/if}
